/* eslint-disable react/require-default-props */
import React, { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import useHideUserSupportBtn from '@hooks/useHideUserSupportBtn';
import { Button } from '@ui';
import useBannerVisibility from './hooks/useBannerVisibility';
import './PageBanner.scss';
function CloseIcon() {
    return (React.createElement("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none" },
        React.createElement("path", { d: "M12 4L4 12M4 4L12 12", stroke: "#3D1A9E", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })));
}
const getPagePattern = (pathname) => {
    if (pathname === '/dashboard' || pathname === '/') {
        return 'dashboard';
    }
    if (pathname.startsWith('/buildings/')) {
        return 'buildings';
    }
    if (pathname.startsWith('/inspections/') && pathname !== '/inspections') {
        return 'inspections-show';
    }
    if (pathname === '/inspections') {
        return 'inspections-index';
    }
    return pathname;
};
function PageBanner({ visibilityUrl, dismissKey, hrefLink, iconPrefix, text, actionButtons, isSticky = false, anchorId }) {
    const [isVisible, setIsVisible] = useState(false);
    const [isTemporarilyDismissed, setIsTemporarilyDismissed] = useState(false);
    useHideUserSupportBtn(isVisible);
    const currentPath = window.location.pathname;
    const pagePattern = getPagePattern(currentPath);
    const fullDismissKey = (dismissKey === null || dismissKey === void 0 ? void 0 : dismissKey.includes('{pagePattern}'))
        ? dismissKey.replace('{pagePattern}', pagePattern)
        : dismissKey;
    const isDismissed = dismissKey && fullDismissKey ? localStorage.getItem(fullDismissKey) === 'true' : false;
    const { data, isFetching: isLoading, error: fetchError } = useBannerVisibility({
        visibilityUrl,
        enabled: !isDismissed && !!visibilityUrl
    });
    useEffect(() => {
        if (isDismissed || isTemporarilyDismissed) {
            setIsVisible(false);
            return;
        }
        if (visibilityUrl) {
            if (!isLoading && !fetchError) {
                setIsVisible((data === null || data === void 0 ? void 0 : data.enabled) || false);
            }
        }
        else {
            setIsVisible(true);
        }
    }, [isDismissed, isTemporarilyDismissed, visibilityUrl, isLoading, fetchError, data]);
    // Track grandparent width for sticky banners
    useEffect(() => {
        if (!isSticky || !isVisible || !anchorId)
            return;
        const updateAnchorWidth = () => {
            // Get the grandparent element by anchorId
            const anchorElement = document.getElementById(anchorId);
            if (!anchorElement)
                return;
            // Get the computed width and set it as CSS custom property
            const { width } = anchorElement.getBoundingClientRect();
            document.documentElement.style.setProperty('--anchor-element-width', `${width}px`);
        };
        // Initial width set
        updateAnchorWidth();
        // Update on window resize
        window.addEventListener('resize', updateAnchorWidth);
        // Use ResizeObserver for more precise tracking
        let resizeObserver = null;
        const anchorParent = document.getElementById(anchorId);
        if (anchorParent && 'ResizeObserver' in window) {
            resizeObserver = new ResizeObserver(() => {
                updateAnchorWidth();
            });
            resizeObserver.observe(anchorParent);
        }
        return () => {
            window.removeEventListener('resize', updateAnchorWidth);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [isSticky, isVisible, anchorId]);
    const handleClose = () => {
        if (dismissKey && fullDismissKey) {
            // Save dismissal preference to localStorage when dismissKey is provided
            try {
                localStorage.setItem(fullDismissKey, 'true');
            }
            catch (error) {
                console.warn('Failed to save banner dismiss preference:', error);
            }
        }
        else {
            // Just temporarily dismiss the banner when no dismissKey is provided
            setIsTemporarilyDismissed(true);
        }
        setIsVisible(false);
    };
    const handleClick = () => {
        if (hrefLink) {
            const newWindow = window.open(hrefLink, '_blank', 'noopener,noreferrer');
            if (newWindow) {
                newWindow.opener = null;
            }
        }
    };
    if (isLoading || !isVisible) {
        return null;
    }
    return (React.createElement("div", { className: `page-banner ${isSticky ? 'page-banner--sticky' : ''}`, onClick: hrefLink ? handleClick : undefined, role: "button", tabIndex: hrefLink ? 0 : undefined, onKeyDown: hrefLink
            ? (e) => {
                if (e.key === 'Enter') {
                    handleClick();
                }
            }
            : undefined },
        React.createElement("div", { className: "page-banner__content", id: "page-banner-content" },
            iconPrefix && (React.createElement("div", { className: "page-banner__icon-wrapper" },
                React.createElement("div", { className: "page-banner__icon" }, iconPrefix))),
            React.createElement("div", { className: "page-banner__text" }, text),
            actionButtons && React.createElement("div", { className: "page-banner__actions" }, actionButtons),
            React.createElement(Button, { className: "page-banner__close", onClick: (e) => {
                    e.stopPropagation();
                    handleClose();
                }, "aria-describedby": "page-banner-content", "aria-label": "Close banner" },
                React.createElement(CloseIcon, null)))));
}
const queryClient = new QueryClient();
export default function PageBannerWrapper(props) {
    return (React.createElement(QueryClientProvider, { client: queryClient },
        React.createElement(PageBanner, Object.assign({}, props))));
}
